import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface SelectedService {
  id: number
  name: string
  duration: number
  price: number
  /** null = "any specialist"; set per service so a cart can mix. */
  staffId: number | null
  staffName: string | null
  /**
   * The heart — frezka's quick-booking "I want the service with this staff!" —
   * for this service's specialist. Per service, because the customer may insist
   * on Alex for the nails and be happy with anyone for the pedicure. It travels
   * as `is_favorite_staff` on the service row, and the booking holding that
   * service wears the heart in the salon's calendar.
   */
  favourite: boolean
}

export interface SelectionState {
  branchId: number | null
  services: SelectedService[]
  /** ISO string of the chosen slot start, in vendor timezone. */
  slotStart: string | null
  couponCode: string | null
  couponDiscount: number
  /** Sent with POST /bookings so a double submit cannot create two bookings. */
  idempotencyKey: string | null
  /**
   * "One specialist for everything" with nobody named: the salon picks one
   * person who can do the whole visit, rather than whoever is free per service.
   * It is the screen's default, so it starts true.
   */
  sameStaff: boolean
}

const initialState: SelectionState = {
  branchId: null,
  services: [],
  slotStart: null,
  couponCode: null,
  couponDiscount: 0,
  idempotencyKey: null,
  sameStaff: true,
}

/** The heart belongs to a specialist the customer actually chose: choosing
 *  somebody else for the service, or nobody, takes it away. */
function assignStaff(svc: SelectedService, staffId: number | null, staffName: string | null) {
  if (svc.staffId !== staffId || staffId === null) svc.favourite = false
  svc.staffId = staffId
  svc.staffName = staffName
}

/** Carts stored before the heart moved onto the services carried one
 *  `favouriteStaffId` for the whole visit; it becomes a heart on each service
 *  booked with that person. */
type StoredSelection = Partial<SelectionState> & {
  favouriteStaffId?: number | null
  services?: (Omit<SelectedService, 'favourite'> & { favourite?: boolean })[]
}

const selectionSlice = createSlice({
  name: 'selection',
  initialState,
  reducers: {
    setBranch(state, action: PayloadAction<number | null>) {
      if (state.branchId !== action.payload) {
        // Services and slots are branch-scoped; keeping them would show prices
        // and times that do not exist at the new branch.
        state.services = []
        state.slotStart = null
      }
      state.branchId = action.payload
    },
    addService(state, action: PayloadAction<SelectedService>) {
      if (state.services.some((s) => s.id === action.payload.id)) return
      state.services.push(action.payload)
      state.slotStart = null
    },
    removeService(state, action: PayloadAction<number>) {
      state.services = state.services.filter((s) => s.id !== action.payload)
      state.slotStart = null
    },
    setServiceStaff(
      state,
      action: PayloadAction<{ serviceId: number; staffId: number | null; staffName: string | null }>,
    ) {
      const svc = state.services.find((s) => s.id === action.payload.serviceId)
      if (!svc) return
      assignStaff(svc, action.payload.staffId, action.payload.staffName)
      // Choosing per service is choosing per service, even "any" for each.
      state.sameStaff = false
      state.slotStart = null
    },
    setStaffForAll(state, action: PayloadAction<{ staffId: number | null; staffName: string | null }>) {
      for (const svc of state.services) {
        assignStaff(svc, action.payload.staffId, action.payload.staffName)
      }
      state.sameStaff = true
      state.slotStart = null
    },
    /** Whether "any specialist" means one person for the whole visit. */
    setSameStaff(state, action: PayloadAction<boolean>) {
      if (state.sameStaff === action.payload) return
      state.sameStaff = action.payload
      state.slotStart = null
    },
    /** The heart on one service. Only a service with a named specialist can wear it. */
    setServiceFavourite(state, action: PayloadAction<{ serviceId: number; favourite: boolean }>) {
      const svc = state.services.find((s) => s.id === action.payload.serviceId)
      if (!svc) return
      svc.favourite = action.payload.favourite && svc.staffId !== null
    },
    /** The heart on every service, for "one specialist for everything". */
    setFavouriteForAll(state, action: PayloadAction<boolean>) {
      for (const svc of state.services) {
        svc.favourite = action.payload && svc.staffId !== null
      }
    },
    setSlot(state, action: PayloadAction<string | null>) {
      state.slotStart = action.payload
    },
    setCoupon(state, action: PayloadAction<{ code: string; discount: number } | null>) {
      state.couponCode = action.payload?.code ?? null
      state.couponDiscount = action.payload?.discount ?? 0
    },
    setIdempotencyKey(state, action: PayloadAction<string | null>) {
      state.idempotencyKey = action.payload
    },
    resetSelection: () => initialState,
    // Spread over the initial state, not straight in: a cart stored before a
    // field existed still rehydrates with that field at its default rather than
    // undefined. sessionStorage outlives a deploy.
    hydrateSelection: (_state, action: PayloadAction<StoredSelection>) => {
      const { favouriteStaffId, services, ...rest } = action.payload
      return {
        ...initialState,
        ...rest,
        services: (services ?? []).map((svc) => ({
          ...svc,
          favourite:
            svc.favourite ?? (favouriteStaffId != null && svc.staffId === favouriteStaffId),
        })),
      }
    },
  },
})

export const {
  setBranch,
  addService,
  removeService,
  setServiceStaff,
  setStaffForAll,
  setSlot,
  setCoupon,
  setIdempotencyKey,
  setSameStaff,
  setServiceFavourite,
  setFavouriteForAll,
  resetSelection,
  hydrateSelection,
} = selectionSlice.actions

export default selectionSlice.reducer

/** Totals live next to the slice so wizard steps and the summary bar agree. */
export const selectTotalDuration = (s: SelectionState) =>
  s.services.reduce((sum, svc) => sum + svc.duration, 0)

export const selectSubtotal = (s: SelectionState) =>
  s.services.reduce((sum, svc) => sum + svc.price, 0)

export const selectTotal = (s: SelectionState) =>
  Math.max(0, selectSubtotal(s) - s.couponDiscount)
